import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { API } from "../lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // {id, title, position}
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ position: 0, duration: 0 });
  const [rate, setRate] = useState(1);
  const saveTimer = useRef(null);

  if (!audioRef.current) {
    audioRef.current = new Audio();
  }

  const saveProgress = useCallback(() => {
    const a = audioRef.current;
    if (!current || !a || !a.duration) return;
    api.post("/audio/progress", {
      lesson_id: current.id,
      position: a.currentTime,
      duration: a.duration || 0,
    }).catch(() => {});
  }, [current]);

  useEffect(() => {
    const a = audioRef.current;
    const onTime = () => setProgress({ position: a.currentTime, duration: a.duration || 0 });
    const onEnd = () => { setPlaying(false); saveProgress(); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [saveProgress]);

  useEffect(() => {
    saveTimer.current = setInterval(saveProgress, 8000);
    return () => clearInterval(saveTimer.current);
  }, [saveProgress]);

  const play = useCallback((lesson) => {
    const a = audioRef.current;
    const token = localStorage.getItem("ill_token");
    const src = `${API}/audio/stream/${lesson.id}?auth=${token}`;
    if (!current || current.id !== lesson.id) {
      a.src = src;
      a.playbackRate = rate;
      setCurrent({ id: lesson.id, title: lesson.title });
      a.addEventListener("loadedmetadata", function once() {
        if (lesson.position) a.currentTime = lesson.position;
        a.removeEventListener("loadedmetadata", once);
      });
    }
    a.play().catch(() => {});
  }, [current, rate]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (a.paused) a.play().catch(() => {}); else { a.pause(); saveProgress(); }
  }, [saveProgress]);

  const seek = useCallback((sec) => {
    const a = audioRef.current;
    a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + sec));
  }, []);

  const seekTo = useCallback((val) => { audioRef.current.currentTime = val; }, []);

  const changeRate = useCallback((r) => {
    setRate(r);
    audioRef.current.playbackRate = r;
  }, []);

  const close = useCallback(() => {
    const a = audioRef.current;
    a.pause();
    saveProgress();
    setCurrent(null);
    setPlaying(false);
  }, [saveProgress]);

  const value = useMemo(
    () => ({ current, playing, progress, rate, play, toggle, seek, seekTo, changeRate, close }),
    [current, playing, progress, rate, play, toggle, seek, seekTo, changeRate, close]
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
